import { ChangeEvent, ReactNode } from 'react'
import { compact } from 'lodash'

import { Checkbox } from '@revolut-internal/ui-kit'
import { PrimaryCellProps, TableColumn, TableRow } from '@revolut-internal/ui-kit-table'

import { EmptyCell } from 'modules/Monitoring/components/TableCells/EmptyCell'
import { ChartData, ChartDataValue, TableDataItem } from 'modules/Monitoring/types'

import { X_AXIS_LABEL_KEY } from '../../consts/chart'
import { formatBlankLabel } from '../formatBlankLabel'
import { formatNumber } from '../formatNumber'

const checkIfEverySelected = (
  rows: TableRow<TableDataItem>[],
  selectedRowIds?: Record<string, boolean>,
) =>
  rows.every(row =>
    row.subRows?.length
      ? checkIfEverySelected(row.subRows, selectedRowIds)
      : selectedRowIds?.[row.id],
  )
const checkIfSomeSelected = (
  rows: TableRow<TableDataItem>[],
  selectedRowIds?: Record<string, boolean>,
) =>
  rows.some(row =>
    row.subRows?.length
      ? checkIfEverySelected(row.subRows, selectedRowIds)
      : selectedRowIds?.[row.id],
  )

export const prepareTableColumnsFromChartData = ({
  data,
  additionalData,
  selectedRowIds,
  firstColumnLabel = 'Name',
  isExpandable = false,
  renderCell,
  onRowSelect,
}: {
  data: ChartData
  additionalData?: ChartData
  selectedRowIds?: Record<string, boolean>
  firstColumnLabel?: string
  isExpandable?: boolean
  renderCell?: (args: {
    cell: PrimaryCellProps<TableDataItem>['cell']
    value: ChartDataValue
  }) => ReactNode
  onRowSelect?: (id: string | string[], value: boolean) => void
}): TableColumn<TableDataItem>[] => {
  const selectColumn: TableColumn<TableDataItem> | undefined =
    data.length || additionalData?.length
      ? {
          id: 'select',
          size: isExpandable ? 80 : 50,
          meta: {
            volatileCell: true,
          },

          header: ({ table }) => {
            const rows = table.getRowModel().rows
            const hasEveryChecked = checkIfEverySelected(rows, selectedRowIds)
            const hasSomeChecked = checkIfSomeSelected(rows, selectedRowIds)

            return (
              <Checkbox
                checked={hasEveryChecked}
                indeterminate={!hasEveryChecked && hasSomeChecked}
                onChange={() => {
                  onRowSelect?.(
                    rows.map(row => row.id),
                    !hasEveryChecked,
                  )
                }}
              />
            )
          },
          cell: ({ row }) => {
            const isNested = Boolean(row.subRows?.length)
            const isChecked = isNested
              ? checkIfEverySelected(row.subRows, selectedRowIds)
              : Boolean(selectedRowIds?.[row.id])
            const hasSomeChecked =
              isNested && checkIfSomeSelected(row.subRows, selectedRowIds)

            const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
              isNested
                ? onRowSelect?.(
                    row.subRows.map(item => item.id),
                    !isChecked,
                  )
                : onRowSelect?.(row.id, event.target.checked)
            }

            return (
              <Checkbox
                checked={isChecked}
                indeterminate={hasSomeChecked}
                onChange={handleChange}
              />
            )
          },
        }
      : undefined

  const dataToIterate =
    (data.length > (additionalData?.length ?? 0) ? data : additionalData) ?? []

  const dataColumns: TableColumn<TableDataItem, number | null>[] = dataToIterate.map(
    item => ({
      id: item.date.toString(),
      minSize: 100,
      header: () => item[X_AXIS_LABEL_KEY],

      accessorFn: row => (row[item.date]?.hasData ? row[item.date].value : null),
      cell: ({ cell, row, getValue }) => {
        const value = getValue()
        const originalValue = row.original[item.date]

        if (!originalValue) {
          return <EmptyCell />
        }

        if (renderCell) {
          return renderCell({
            // PrimaryCell expects Cell<TableDataItem, unknown>, while this column cell is
            // Cell<TableDataItem, number | null>. TanStack Cell is invariant in Value.
            cell: cell as PrimaryCellProps<TableDataItem>['cell'],
            value: originalValue,
          })
        }

        return formatNumber(value)
      },
      enableSorting: true,
    }),
  )

  return compact([
    selectColumn,
    {
      header: firstColumnLabel,
      accessorKey: 'name',
      minSize: 200,
      cell: ({ getValue }) => formatBlankLabel(getValue()),

      enableSorting: true,
    },
    ...dataColumns,
  ])
}
